import React, { useEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import About from "@/components/About";
import MenuViewer from "@/components/MenuViewer";
import Grill from "@/components/Grill";
import Testimonials from "@/components/Testimonials";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";

function AdminSessionGuard() {
  // Logs the admin out whenever the URL leaves the /admin (dashboard) route.
  // This means clicking "Site", clicking the browser back button, or navigating
  // to any non-admin path forces the admin to re-enter credentials next time.
  const { logout, user } = useAuth();
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    const wasOnDashboard = prevPath.current === "/admin";
    const nowOnDashboard = location.pathname === "/admin";
    if (wasOnDashboard && !nowOnDashboard && user) {
      logout();
    }
    prevPath.current = location.pathname;
  }, [location.pathname, logout, user]);

  return null;
}

function PublicSite() {
  const { loading, content } = useSiteContent();
  if (loading || !content) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center" data-testid="site-loading">
        <div className="text-[color:var(--gold-hi)] font-serif-display text-2xl italic">
          Warming up the grill...
        </div>
      </div>
    );
  }
  return (
    <div className="App grain min-h-screen bg-bg" data-testid="app-root">
      <Nav />
      <main>
        <Hero />
        <About />
        <MenuViewer />
        <Grill />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center" data-testid="auth-loading">
        <div className="text-[color:var(--gold-hi)] font-serif-display text-xl italic">
          Checking credentials...
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  React.useEffect(() => {
    document.title = "Aseka's Mtaani · Nairobi Grill House";
    const observer = new MutationObserver(() => {
      if (document.title !== "Aseka's Mtaani · Nairobi Grill House") {
        document.title = "Aseka's Mtaani · Nairobi Grill House";
      }
    });
    const titleEl = document.querySelector("title");
    if (titleEl) observer.observe(titleEl, { childList: true });
    return () => observer.disconnect();
  }, []);

  return (
    <Routes>
      <Route path="/" element={<><AdminSessionGuard /><PublicSite /></>} />
      <Route path="/admin/login" element={<><AdminSessionGuard /><AdminLogin /></>} />
      <Route
        path="/admin"
        element={
          <>
            <AdminSessionGuard />
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          </>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
